/**
 * nutritionEngine.ts
 * ---------------------------------------------------------------------------
 * Single source of truth for all pregnancy nutrition targets used across
 * PregTrack. Every number below is a named constant with an inline citation.
 * Do NOT hardcode nutrition numbers anywhere else in the app — import from
 * here.
 *
 * PregTrack is a support tool, not a diagnostic one. All target/status
 * language is descriptive ("below target" / "on track" / "above target"),
 * never clinical ("deficiency", "excess", "risk"). Any user-facing copy that
 * reads these values should preserve that framing.
 *
 * PRIMARY SOURCES (as of this module's last review):
 *  - [IOM-DRI] National Academies of Sciences, Engineering, and Medicine /
 *    Institute of Medicine, Dietary Reference Intakes (DRI) tables for
 *    Energy, Macronutrients, and select Micronutrients (Vitamins & Elements),
 *    pregnancy/lactation life-stage columns.
 *  - [IOM-WG] IOM & National Research Council, "Weight Gain During
 *    Pregnancy: Reexamining the Guidelines" (2009) — gestational weight-gain
 *    ranges by pre-pregnancy BMI category.
 *  - [ACOG-NUTR] American College of Obstetricians and Gynecologists,
 *    "Nutrition During Pregnancy" (Patient FAQ / Committee guidance).
 *  - [ACOG-CAFF] ACOG Committee Opinion, "Moderate Caffeine Consumption
 *    During Pregnancy."
 *  - [ACOG-ALC] ACOG guidance on alcohol use during pregnancy (no
 *    established safe level; abstinence advised).
 *  - [ACOG-OMEGA3] ACOG guidance on omega-3 fatty acid / DHA intake in
 *    pregnancy.
 *
 * IMPORTANT: This module encodes general population guidance. It is not a
 * substitute for individualized advice from the user's OB/GYN or midwife,
 * and the app must say so wherever these numbers are surfaced. Values here
 * should be reviewed periodically against current ACOG/IOM publications
 * since nutrition guidance is occasionally revised.
 */

// ---------------------------------------------------------------------------
// Trimester & age bands
// ---------------------------------------------------------------------------

export type Trimester = 1 | 2 | 3;

/** [ACOG-NUTR] Standard trimester week boundaries used clinically. */
export const TRIMESTER_WEEK_RANGES: Record<Trimester, [number, number]> = {
  1: [1, 13],
  2: [14, 27],
  3: [28, 40],
};

export function trimesterForWeek(gestationalWeek: number): Trimester {
  if (gestationalWeek <= 13) return 1;
  if (gestationalWeek <= 27) return 2;
  return 3;
}

export type AgeBand = "14-18" | "19-50";

export function ageBandForAge(ageYears: number): AgeBand {
  return ageYears >= 14 && ageYears <= 18 ? "14-18" : "19-50";
}

// ---------------------------------------------------------------------------
// Energy (calories)
// ---------------------------------------------------------------------------

/**
 * [IOM-DRI] Estimated Energy Requirement (EER) adjustment for pregnancy,
 * added on top of the person's non-pregnant EER: no increase in T1, +340
 * kcal/day in T2, +452 kcal/day in T3.
 */
export const CALORIE_ADJUSTMENT_BY_TRIMESTER: Record<Trimester, number> = {
  1: 0,
  2: 340,
  3: 452,
};

/**
 * [ACOG-NUTR] For twin/multiple gestations, an additional ~300 kcal/day per
 * extra fetus (beyond the first) is commonly used as a practical estimate
 * once energy needs rise in T2/T3. Applied only from T2 onward, consistent
 * with how the singleton increment itself only begins in T2.
 */
export const MULTIPLES_CALORIE_BONUS_PER_EXTRA_FETUS = 300;

export function calorieTarget(params: {
  nonPregnantEerKcal: number;
  trimester: Trimester;
  fetusCount: number;
}): number {
  const { nonPregnantEerKcal, trimester, fetusCount } = params;
  const base = nonPregnantEerKcal + CALORIE_ADJUSTMENT_BY_TRIMESTER[trimester];
  const extraFetuses = Math.max(0, fetusCount - 1);
  const multiplesBonus =
    trimester === 1 ? 0 : extraFetuses * MULTIPLES_CALORIE_BONUS_PER_EXTRA_FETUS;
  return Math.round(base + multiplesBonus);
}

/**
 * Mifflin-St Jeor equation, used to estimate the person's non-pregnant
 * baseline EER from pre-pregnancy weight/height/age, then scaled by an
 * activity factor. This is a standard, widely validated resting-energy
 * estimate — not itself a pregnancy-specific figure — used only as the
 * baseline that CALORIE_ADJUSTMENT_BY_TRIMESTER is added to.
 */
const ACTIVITY_FACTORS: Record<string, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
};

export function estimateNonPregnantEer(params: {
  weightKg: number;
  heightCm: number;
  ageYears: number;
  activityLevel: keyof typeof ACTIVITY_FACTORS;
}): number {
  const { weightKg, heightCm, ageYears, activityLevel } = params;
  // Mifflin-St Jeor, female coefficients.
  const bmr = 10 * weightKg + 6.25 * heightCm - 5 * ageYears - 161;
  return Math.round(bmr * ACTIVITY_FACTORS[activityLevel]);
}

// ---------------------------------------------------------------------------
// Macronutrients
// ---------------------------------------------------------------------------

/** [IOM-DRI] RDA for protein during pregnancy, all age bands: 71 g/day. */
export const PROTEIN_TARGET_G_PER_DAY = 71;

/** [IOM-DRI] RDA for digestible carbohydrate during pregnancy: 175 g/day. */
export const CARB_TARGET_G_PER_DAY = 175;

/**
 * [IOM-DRI] No pregnancy-specific fat RDA exists; the Acceptable
 * Macronutrient Distribution Range (AMDR) of 20-35% of total calories
 * (general adult population) is used, expressed here as a fraction range
 * applied to the person's calorie target.
 */
export const FAT_PERCENT_OF_CALORIES_RANGE: [number, number] = [0.2, 0.35];

/** [IOM-DRI] Adequate Intake (AI) for fiber during pregnancy: 28 g/day. */
export const FIBER_TARGET_G_PER_DAY = 28;

export function macroTargets(calorieGoal: number) {
  const [fatLowPct, fatHighPct] = FAT_PERCENT_OF_CALORIES_RANGE;
  return {
    proteinG: PROTEIN_TARGET_G_PER_DAY,
    carbG: CARB_TARGET_G_PER_DAY,
    fiberG: FIBER_TARGET_G_PER_DAY,
    // 9 kcal/g of fat.
    fatGRange: [
      Math.round((calorieGoal * fatLowPct) / 9),
      Math.round((calorieGoal * fatHighPct) / 9),
    ] as [number, number],
  };
}

// ---------------------------------------------------------------------------
// Micronutrients
// ---------------------------------------------------------------------------

export interface MicronutrientTarget {
  label: string;
  unit: string;
  /** True for the nutrients most commonly under-consumed in pregnancy,
   *  surfaced prominently on the daily dashboard per the product spec. */
  highlightOnDashboard: boolean;
  amount: number;
}

/**
 * [IOM-DRI] Micronutrient targets during pregnancy, by age band.
 * Sources for each row are the corresponding IOM DRI table for that
 * nutrient's pregnancy life-stage group, cross-checked against
 * [ACOG-NUTR] and [ACOG-OMEGA3] for DHA specifically (IOM sets AI for
 * total omega-3 ALA; ACOG's DHA-specific guidance is used here because
 * DHA is the nutrient PregTrack surfaces).
 */
export const MICRONUTRIENT_TARGETS: Record<AgeBand, Record<string, MicronutrientTarget>> = {
  "19-50": {
    folate: { label: "Folate (DFE)", unit: "mcg", highlightOnDashboard: true, amount: 600 },
    iron: { label: "Iron", unit: "mg", highlightOnDashboard: true, amount: 27 },
    calcium: { label: "Calcium", unit: "mg", highlightOnDashboard: true, amount: 1000 },
    iodine: { label: "Iodine", unit: "mcg", highlightOnDashboard: true, amount: 220 },
    choline: { label: "Choline", unit: "mg", highlightOnDashboard: true, amount: 450 },
    omega3Dha: { label: "Omega-3 DHA", unit: "mg", highlightOnDashboard: true, amount: 250 },
    vitaminA: { label: "Vitamin A", unit: "mcg RAE", highlightOnDashboard: false, amount: 770 },
    vitaminC: { label: "Vitamin C", unit: "mg", highlightOnDashboard: false, amount: 85 },
    vitaminD: { label: "Vitamin D", unit: "mcg", highlightOnDashboard: false, amount: 15 },
    vitaminB6: { label: "Vitamin B6", unit: "mg", highlightOnDashboard: false, amount: 1.9 },
    vitaminB12: { label: "Vitamin B12", unit: "mcg", highlightOnDashboard: false, amount: 2.6 },
  },
  "14-18": {
    folate: { label: "Folate (DFE)", unit: "mcg", highlightOnDashboard: true, amount: 600 },
    iron: { label: "Iron", unit: "mg", highlightOnDashboard: true, amount: 27 },
    calcium: { label: "Calcium", unit: "mg", highlightOnDashboard: true, amount: 1300 },
    iodine: { label: "Iodine", unit: "mcg", highlightOnDashboard: true, amount: 220 },
    choline: { label: "Choline", unit: "mg", highlightOnDashboard: true, amount: 450 },
    omega3Dha: { label: "Omega-3 DHA", unit: "mg", highlightOnDashboard: true, amount: 250 },
    vitaminA: { label: "Vitamin A", unit: "mcg RAE", highlightOnDashboard: false, amount: 750 },
    vitaminC: { label: "Vitamin C", unit: "mg", highlightOnDashboard: false, amount: 80 },
    vitaminD: { label: "Vitamin D", unit: "mcg", highlightOnDashboard: false, amount: 15 },
    vitaminB6: { label: "Vitamin B6", unit: "mg", highlightOnDashboard: false, amount: 1.9 },
    vitaminB12: { label: "Vitamin B12", unit: "mcg", highlightOnDashboard: false, amount: 2.6 },
  },
};

// ---------------------------------------------------------------------------
// Gestational weight gain
// ---------------------------------------------------------------------------

export type BmiCategory = "underweight" | "normal" | "overweight" | "obese";

export function bmiCategoryFor(bmi: number): BmiCategory {
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "normal";
  if (bmi < 30) return "overweight";
  return "obese";
}

export function calculateBmi(weightKg: number, heightCm: number): number {
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

export interface WeightGainBand {
  /** Total recommended gain over the full pregnancy, in kg. */
  totalRangeKg: [number, number];
  /** Typical rate of gain per week during T2/T3, in kg. Used to draw the
   *  weekly tolerance band on the weight trend chart. T1 gain is usually
   *  small (roughly 0.5-2 kg total) and isn't rate-banded per week. */
  weeklyRateRangeKgT2T3: [number, number];
}

/**
 * [IOM-WG] Total gestational weight gain ranges for singleton pregnancies,
 * by pre-pregnancy BMI category. Values converted from the source's pounds
 * to kg (1 lb = 0.453592 kg) and rounded to 1 decimal place.
 */
export const SINGLETON_WEIGHT_GAIN_BANDS: Record<BmiCategory, WeightGainBand> = {
  underweight: { totalRangeKg: [12.5, 18.0], weeklyRateRangeKgT2T3: [0.44, 0.58] },
  normal: { totalRangeKg: [11.5, 16.0], weeklyRateRangeKgT2T3: [0.35, 0.5] },
  overweight: { totalRangeKg: [7.0, 11.5], weeklyRateRangeKgT2T3: [0.23, 0.33] },
  obese: { totalRangeKg: [5.0, 9.0], weeklyRateRangeKgT2T3: [0.17, 0.27] },
};

/**
 * [IOM-WG] Total gestational weight gain ranges for twin pregnancies, by
 * pre-pregnancy BMI category. IOM did not establish an evidence-based range
 * for underweight twin pregnancies at time of publication; PregTrack falls
 * back to the normal-weight twin band in that case and surfaces this as an
 * estimate, deferring further to the user's provider.
 */
export const TWIN_WEIGHT_GAIN_BANDS: Record<BmiCategory, WeightGainBand> = {
  underweight: { totalRangeKg: [16.8, 24.5], weeklyRateRangeKgT2T3: [0.62, 0.91] }, // estimate, see note above
  normal: { totalRangeKg: [16.8, 24.5], weeklyRateRangeKgT2T3: [0.62, 0.91] },
  overweight: { totalRangeKg: [14.1, 22.7], weeklyRateRangeKgT2T3: [0.52, 0.84] },
  obese: { totalRangeKg: [11.3, 19.1], weeklyRateRangeKgT2T3: [0.42, 0.71] },
};

export function weightGainBandFor(params: {
  bmiCategory: BmiCategory;
  fetusCount: number;
}): WeightGainBand {
  const { bmiCategory, fetusCount } = params;
  // No published IOM band for triplets+; use the twin band as a conservative
  // estimate and let the UI flag it as approximate, deferring to the
  // provider (consistent with the app's "support, never diagnose" stance).
  return fetusCount >= 2 ? TWIN_WEIGHT_GAIN_BANDS[bmiCategory] : SINGLETON_WEIGHT_GAIN_BANDS[bmiCategory];
}

// ---------------------------------------------------------------------------
// Caffeine & alcohol — soft, informational thresholds only
// ---------------------------------------------------------------------------

/** [ACOG-CAFF] ACOG advises limiting caffeine to under 200 mg/day. */
export const CAFFEINE_SOFT_LIMIT_MG_PER_DAY = 200;

/**
 * [ACOG-ALC] No amount of alcohol has been established as safe during
 * pregnancy; ACOG advises abstaining entirely. PregTrack treats any logged
 * alcohol as "above target" without further gradation, and copy referencing
 * this constant must stay informational ("above target"), never diagnostic.
 */
export const ALCOHOL_SOFT_LIMIT_STD_DRINKS_PER_DAY = 0;

export type ThresholdStatus = "below" | "on-track" | "above";

/**
 * Generic status classifier used across the dashboard for any tracked
 * value against a target/band. Intentionally produces only the three
 * neutral, non-clinical labels described in this module's header comment.
 */
export function statusFor(value: number, target: number, tolerancePct = 0.1): ThresholdStatus {
  const low = target * (1 - tolerancePct);
  const high = target * (1 + tolerancePct);
  if (value < low) return "below";
  if (value > high) return "above";
  return "on-track";
}
